import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { MessageSquare, User, Calendar, LogOut, Menu, ChevronDown } from 'lucide-react';
import CollapsibleSidebar from './CollapsibleSidebar';
import textConfig from '../config/textConfig';
import '../styles/Layout.css';

// Helper function to get proper image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  
  // Check if it's already a complete URL (starts with http:// or https://)
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // Otherwise, it's a relative path, so prepend the origin
  return `${window.location.origin}${imagePath}`;
};

const Layout = () => {
  const { user, logout, isAutoLoginMode } = useAuth();
  const { userRoles } = usePermissions();
  useLocation(); // subscribes Layout to route changes
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const profileMenuRef = useRef(null);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Update document title based on user role
  useEffect(() => {
    document.title = (user?.role === 'admin' || user?.role === 'super_admin' || user?.is_admin) 
      ? `${textConfig.appName} Admin` 
      : textConfig.appName;
  }, [user]);

  // Handle window resize and sidebar collapsed state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Check localStorage for sidebar collapsed state
    const checkSidebarState = () => {
      const collapsed = localStorage.getItem('sidebarCollapsed') === 'true';
      setSidebarCollapsed(collapsed);
    };

    // Listen for storage changes (when sidebar is toggled)
    const handleStorageChange = (e) => {
      if (e.key === 'sidebarCollapsed') {
        setSidebarCollapsed(e.newValue === 'true');
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('storage', handleStorageChange);
    
    // Check initial state
    checkSidebarState();
    
    // Also listen for custom event from same window
    const handleSidebarToggle = () => {
      checkSidebarState();
    };
    window.addEventListener('sidebarToggled', handleSidebarToggle);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('sidebarToggled', handleSidebarToggle);
    };
  }, []);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleProfileMenu = () => {
    setProfileMenuOpen(!profileMenuOpen);
  };

  const menuButtonClasses = `menu-button ${isMobile ? 'menu-button-visible' : 'menu-button-desktop-hidden'}`;

  // Check if user should see sidebar
  // Show sidebar if:
  // 1. user.role is admin/super_admin, OR
  // 2. user has any assigned role other than just "user" (from permissions system)
  const hasElevatedRole = useMemo(() => {
    // Check user.role field first
    if (user?.role === 'admin' || user?.role === 'super_admin' || user?.is_admin) {
      return true;
    }

    // Check actual assigned roles from permission system
    // If user has any role other than "user", they should see the sidebar
    if (userRoles && userRoles.length > 0) {
      const hasNonUserRole = userRoles.some(role =>
        role.name !== 'user' && role.is_active !== false
      );
      if (hasNonUserRole) {
        return true;
      }
    }

    return false;
  }, [user?.role, user?.is_admin, userRoles]);

  // Hide sidebar only for regular users with no elevated roles
  const isRegularUser = !hasElevatedRole;

  return (
    <div className="layout">
      {/* Only show sidebar if NOT a regular user */}
      {!isRegularUser && (
        <CollapsibleSidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
      )}

      <div
        className={`layout-with-sidebar ${isMobile ? 'layout-mobile' : ''}`}
        style={{
          marginLeft: !isMobile && !isRegularUser ? (sidebarCollapsed ? '80px' : '240px') : '0',
          transition: 'margin-left 0.3s ease'
        }}
      >
        <header className={`header ${isMobile ? 'header-mobile' : ''}`}>
          <div className="container">
            <div className="app-navbar-content">
              <div className="app-navbar-left">
                {/* Only show hamburger menu if NOT a regular user */}
                {!isRegularUser && (
                  <button
                    className={menuButtonClasses}
                    onClick={toggleSidebar}
                    aria-label="Toggle menu"
                    style={{ minWidth: '40px', minHeight: '40px' }}
                  >
                    <Menu size={24} />
                  </button>
                )}

                <Link
                  to={isRegularUser ? "/chat" : "/"}
                  className="header-title"
                  aria-label={`${textConfig.appName} home`}
                >
                  <span className="header-title-text">{textConfig.appName}.</span>
                </Link>
              </div>
              
              <div className="app-navbar-actions">
                <div className="profile-menu" ref={profileMenuRef}>
                  <button 
                    className="profile-button"
                    onClick={toggleProfileMenu}
                  >
                    <div className="profile-avatar">
                      {user?.user_image ? (
                        <img 
                          src={getImageUrl(user.user_image)} 
                          alt={user?.first_name || 'User'}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className="profile-avatar-icon" style={{ display: user?.user_image ? 'none' : 'flex' }}>
                        <User size={20} />
                      </div>
                    </div>
                    <span 
                      className="profile-name"
                      title={user?.first_name || 'User'}
                    >
                      {user?.first_name || 'User'}
                    </span>
                    <ChevronDown size={16} className={`profile-chevron ${profileMenuOpen ? 'rotate' : ''}`} />
                  </button>
                  
                  {profileMenuOpen && (
                    <div className={`profile-dropdown ${isMobile ? 'profile-dropdown-mobile' : ''}`}>
                      {isRegularUser && (
                        <>
                          <Link
                            to="/chat"
                            className="dropdown-item"
                            onClick={() => setProfileMenuOpen(false)}
                          >
                            <MessageSquare size={16} />
                            <span>Chat</span>
                          </Link>
                          <Link
                            to="/google-calendar"
                            className="dropdown-item"
                            onClick={() => setProfileMenuOpen(false)}
                          >
                            <Calendar size={16} />
                            <span>Calendar</span>
                          </Link>
                          <div className="dropdown-divider"></div>
                        </>
                      )}
                      <Link
                        to="/profile"
                        className="dropdown-item"
                        onClick={() => setProfileMenuOpen(false)}
                      >
                        <User size={16} />
                        <span>My Account</span>
                      </Link>
                      {!isAutoLoginMode && (
                        <>
                          <div className="dropdown-divider"></div>
                          <button
                            onClick={handleLogout}
                            className="dropdown-item dropdown-logout"
                          >
                            <LogOut size={16} />
                            <span>Logout</span>
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="main">
          <div className="container">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
